import React, {useEffect, useRef, useState} from 'react';
import {useRouter} from 'next/router';
import {
  ArrowDownIcon,
  Box,
  Button,
  Pressable,
  TickIcon,
  Typography,
} from '@ttx/design-system';
import {usePlausible} from 'next-plausible';
import {Collection, NftToken} from '../../server/router/collection/types';
import {CollectionView} from './artist-collection-list';
import {ArtistNFTPage} from './artistNFTList';
import {useOutsideClick} from '../../hooks/use-outside-click';
import {useTtxQuery} from '../../hooks/ttx-trpc';
import { getArtistListingUrl } from '../../utils/polymesh-consts';
import { useToastContext } from '../../contexts/toast-context';

interface CollectionNFTViewProps {
  collectionList: Collection[];
  nftView?: React.ReactElement;
  isMobile: boolean;
  hasBorder?: boolean;
  isStudio?: boolean;
  hideCurrency?: boolean;
}

type ViewType = {
  id: string;
  title: string;
};

const DropDownMobileView = ({
  setShowMenu,
  setView,
  view,
  collectionViewList,
}: {
  setShowMenu: (arg: boolean) => void;
  setView: (arg: ViewType) => void;
  view: ViewType;
  collectionViewList: any[];
}) => {
  const [selectedView, setSelectedView] = useState<ViewType>(view);
  const wrapperRef = useRef(null);

  const actionClose = () => {
    setShowMenu(false);
  };

  const onApply = () => {
    setView(selectedView);
    setShowMenu(false);
  };

  useOutsideClick(wrapperRef, actionClose);

  return (
    <Box
      style={
        {
          position: 'fixed',
          width: '100vw',
          height: '100%',
          background: 'var(--ttx-colors-backgroundBlur)',
          zIndex: 1,
          top: '0px',
          left: '0px',
          overFlow: 'hidden',
        } as any
      }
      css={{
        'backdrop-filter': 'blur(20px)',
      }}
    >
      <Box style={{height: '100%', width: '100%', touchAction: 'none'} as any}>
        <Box
          bg="surfaceDark"
          marginTop="one"
          zIndex={1}
          bottom="0px"
          position="absolute"
          width="100vw"
          ref={wrapperRef}
        >
          <Box
            width="100%"
            borderBottomColor="outlines"
            borderBottomWidth="one"
            borderBottomStyle="solid"
            padding="three"
          >
            <Typography color="textLow" fontSize="h2">
              Collections
            </Typography>
          </Box>
          {collectionViewList.map((e) => (
            <Pressable
              onPress={() => {
                setSelectedView(e);
              }}
            >
              <Box
                width="100%"
                borderBottomColor="outlines"
                borderBottomWidth="one"
                borderBottomStyle="solid"
                padding="three"
              >
                <Typography color="textDefault" fontSize="h2">
                  {e.title}
                </Typography>
                {selectedView.id === e.id && (
                  <Box position="absolute" right="10px">
                    <TickIcon color="outlines" size={30} />
                  </Box>
                )}
              </Box>
            </Pressable>
          ))}

          <Button
            type="primary"
            size="S"
            buttonStyle={{margin: '8px 6px 8px 3px'}}
            onPress={onApply}
          >
            Apply
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export const CollectionNFTView: React.FC<CollectionNFTViewProps> = ({
  collectionList,
  nftView,
  isMobile,
  hasBorder,
  isStudio,
  hideCurrency = false,
}: CollectionNFTViewProps) => {
  const router = useRouter();
  const plausible = usePlausible();
  const [view, setView] = useState<ViewType>({title: 'See All', id: 'ALL'});
  const [collectionViewList, setCollectionViewList] = useState<ViewType[]>([
    {title: 'See All', id: 'ALL'},
  ]);
  const [nftList, setNftList] = useState<NftToken[] | null>(null);
  const [showMenu, setShowMenu] = useState(false);
  const toast = useToastContext();
  const artistSlug = router.query.slug as string;
  
  const {data: whitelistedArtistList} = useTtxQuery(['traxx.getWhitelistArtist'],
    {
      onError: (err) => {
        toast.addErrorToast('Failed to load artist details. Please try again later.');
      }
    }
  );

  const artistListingUrl = getArtistListingUrl(whitelistedArtistList || [], artistSlug);

  
  useEffect(() => {
    if (collectionList.length) {
      const colList: ViewType[] = [{title: 'See All', id: 'ALL'}];
      collectionList.forEach((e) => {
        colList.push({title: e.title, id: e._id});
      });
      setCollectionViewList(colList);
    }
  }, [collectionList]);

  useEffect(() => {
    if (view.id !== 'ALL') {
      const collectionNFTs = collectionList.find((e) => e._id === view.id);
      if (collectionNFTs) {
        const nfts = collectionNFTs.nftTokenIds.map((item) => ({
          ...item,
          network: collectionList[0].blockchainNetworkDetail.network,
          contractAddress: collectionList[0].contractAddress,
          multiCopy: collectionList[0].multiCopy,
          type: collectionList[0].type,
        }));
        setNftList(nfts);
      }
    } else {
      setNftList(null);
    }
  }, [view]);

  if (!collectionList.length) {
    return null;
  }

  const onExploreCollection = (e: ViewType) => {
    setView(e);
    plausible('Explore Collection CTA Selected');
  };

  return (
    <>
      {showMenu && isMobile && (
        <DropDownMobileView
          setShowMenu={(arg) => {
            plausible('Collection Grouping Chip Selected');
            setShowMenu(arg);
          }}
          setView={(arg) => {
            if (arg.id !== 'ALL') {
              plausible('Collection Selected From Dropdown List');
            }
            setView(arg);
          }}
          view={view}
          collectionViewList={collectionViewList}
        />
      )}
      <Box minHeight="200px" marginBottom="ten">
        <Box width="100%" height={['64px']} zIndex={1}>
          <Box
            width={['95%', 'auto']}
            minWidth="180px"
            position={['relative', 'absolute']}
            right={['0px', '10px']}
            margin="three"
            paddingTop={['three', 'zero']}
            zIndex={1}
            display="flex"
            flexDirection="row"
            justifyContent="flex-start"
          >
            {artistListingUrl && (
              <Button
                type="secondary"
                hasBorder
                size="S"
                onPress={() => {
                  window.location.href = artistListingUrl;
                }}
                style={{
                  alignSelf: 'flex-start',
                  marginRight: '8px',
                  flexGrow: 1,
                }}
              >
                View On Polymesh
              </Button>
            )}
            <Button
              type="secondary"
              hasBorder
              size="S"
              onPress={() => {
                setShowMenu(!showMenu);
                if (!showMenu) {
                  plausible('Collection Grouping Chip Selected');
                }
              }}
              rightAccessory={
                showMenu ? (
                  <ArrowDownIcon color="outlines" />
                ) : (
                  <ArrowDownIcon color="outlines" />
                )
              }
              style={{alignSelf: 'flex-start', flexGrow: 1}}
            >
              {view.title}
            </Button>
            {showMenu && !isMobile && (
              <Pressable
                onPress={() => {
                  setShowMenu(false);
                }}
              >
                <Box
                  style={
                    {
                      position: 'fixed',
                      width: '100vw',
                      height: '100%',
                      background: 'var(--ttx-colors-transparent)',
                      zIndex: 1,
                      top: '0px',
                      left: '0px',
                      overFlow: 'hidden',
                    } as any
                  }
                />
              </Pressable>
            )}
            {showMenu && !isMobile && (
              <Box
                borderColor="outlines"
                borderWidth="one"
                borderRadius="two"
                borderStyle="solid"
                bg="backgroundBlur"
                marginTop="one"
                zIndex={2}
                position="absolute"
                top="100%"
                left="0"
                width="100%"
              >
                <Box
                  height="42px"
                  width="100%"
                  borderBottomColor="grey24"
                  borderBottomWidth="one"
                  borderBottomStyle="solid"
                  paddingTop="three"
                  paddingLeft="three"
                >
                  <Typography color="textLow">Collections</Typography>
                </Box>
                {collectionViewList.map((e) => (
                  <Pressable
                    onPress={() => {
                      setView(e);
                      if (e.id !== 'ALL') {
                        plausible('Collection Selected From Dropdown List');
                      }
                      setShowMenu(false);
                    }}
                  >
                    <Box
                      height="42px"
                      width="100%"
                      borderBottomColor="grey24"
                      borderBottomWidth="one"
                      borderBottomStyle="solid"
                      paddingTop="three"
                      paddingLeft="three"
                    >
                      <Typography color="textDefault">{e.title}</Typography>
                      {view.id === e.id && (
                        <Box position="absolute" right="10px">
                          <TickIcon color="outlines" size={14} />
                        </Box>
                      )}
                    </Box>
                  </Pressable>
                ))}
              </Box>
            )}
          </Box>
          <br />
        </Box>
        {view.id === 'ALL' && (
          <CollectionView
            collectionList={collectionList}
            isMobile={isMobile}
            onExploreCollection={onExploreCollection}
            isStudio={isStudio}
          />
        )}
        {view.id !== 'ALL' && nftList && (
          <Box paddingTop="eight">
            <ArtistNFTPage
              data={nftList}
              hasBorder={hasBorder}
              hideCurrency={hideCurrency}
            />
          </Box>
        )}
      </Box>
    </>
  );
};
